import { useRef } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types/auth';
import { clearLogoutFlag, hasLogoutFlag } from '@/utils/auth';
import { useBlockGuestHomeForDeepLink } from '@/utils/appDeepLinkLaunch';
import { getAppLaunchHomePath } from '@/utils/redirectByRole';
import { canBrowseAsGuest } from '@/utils/guestMode';
import { validateReturnUrl } from '@/utils/returnUrl';
import {
  getCoHostHomePath,
  shouldKickCoHostToHost,
  shouldKickCommunityToHost,
} from '@/utils/coHostSession';

function AuthSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export const GuestOnly = () => {
  const { isAuthenticated, isLoading, user, auth } = useAuth();
  const [searchParams] = useSearchParams();
  const blockGuestHome = useBlockGuestHomeForDeepLink();
  const renderedAsGuestRef = useRef(false);

  if (isLoading || (blockGuestHome && isAuthenticated)) {
    return <AuthSpinner />;
  }

  if (isAuthenticated && user) {
    // Login happens on `/login` while this route is mounted. AuthContext navigates
    // to role home; do not replace that mid-flight.
    if (renderedAsGuestRef.current) {
      return <AuthSpinner />;
    }
    clearLogoutFlag();
    const destination =
      validateReturnUrl(searchParams.get('returnUrl'), user.role) ??
      getAppLaunchHomePath(user.role, auth?.slug);

    if (destination.startsWith('http://') || destination.startsWith('https://')) {
      window.location.replace(destination);
      return <AuthSpinner />;
    }

    return <Navigate to={destination} replace />;
  }

  if (!isAuthenticated && hasLogoutFlag() && searchParams.has('returnUrl')) {
    return <Navigate to="/login" replace />;
  }

  renderedAsGuestRef.current = true;
  return <Outlet />;
};

export const RequireAuth = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (canBrowseAsGuest(location.pathname)) {
      return <Outlet />;
    }
    if (hasLogoutFlag()) {
      return <Navigate to="/login" replace />;
    }
    // Desktop web (no guest mode): bare `/` opens login without returnUrl noise
    if (location.pathname === '/' || location.pathname === '') {
      return <Navigate to="/login" replace />;
    }
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <Outlet />;
};

interface RequireRoleProps {
  allowedRoles: UserRole[];
}

export const RequireRole = ({ allowedRoles }: RequireRoleProps) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated && canBrowseAsGuest(location.pathname)) {
    return <Outlet />;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6">
        <h1 className="text-2xl font-bold text-foreground">Akses Ditolak</h1>
        <p className="text-muted-foreground">Anda tidak memiliki izin untuk mengakses halaman ini.</p>
      </div>
    );
  }

  return <Outlet />;
};

export const RedirectCoHostFromCustomer = () => {
  const { isCoHost, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AuthSpinner />;
  }

  if (shouldKickCoHostToHost(isCoHost, location.pathname)) {
    return <Navigate to={getCoHostHomePath()} replace />;
  }

  if (shouldKickCommunityToHost(user?.role, location.pathname)) {
    return <Navigate to={getCoHostHomePath()} replace />;
  }

  return <Outlet />;
};
